<script setup>
import { reactive, computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { useToastStore } from '@/components/helper/toast.js'
import { useConfigStore } from '@/stores/config.js'

const auth = useAuthStore()
const { loading, error } = storeToRefs(auth)
const toast = useToastStore()
const configStore = useConfigStore()

const form = reactive({
  username: '',
  password: '',
})

const canSubmit = computed(() => form.username.trim() && form.password.trim() && !loading.value)
const showPassword = ref(false)

function togglePassword() {
  showPassword.value = !showPassword.value
}

async function handleSubmit() {
  if (!canSubmit.value) return

  try {
    const result = await auth.login({
      username: form.username.trim(),
      password: form.password,
    })

    toast.show({
      message: result?.message || 'Login succeeded.',
      type: 'success',
      position: 'top-center',
      timeout: 3000,
    })
  } catch (err) {
    toast.show({
      message: err.message || 'Login failed.',
      type: 'danger',
      position: 'top-center',
      timeout: 4000,
    })
  }
}
</script>

<template>
  <div class="login-screen">
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <div class="card-shell">
      <div class="card-content">
        <section class="form-panel">
          <h2>Sign In</h2>
          <p>Use an administrator account to access fleet data.</p>

          <form class="form-shell" @submit.prevent="handleSubmit">
            <div class="form-field">
              <label for="username">Username</label>
              <div class="input-wrapper">
                <i class="bi bi-person"></i>
                <input
                  id="username"
                  v-model="form.username"
                  type="text"
                  autocomplete="username"
                  placeholder="Username"
                  required
                />
              </div>
            </div>

            <div class="form-field">
              <label for="password">Password</label>
              <div class="input-wrapper password">
                <i class="bi bi-lock"></i>
                <input
                  id="password"
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="current-password"
                  placeholder="Password"
                  required
                />
                <button type="button" class="toggle-password" @click="togglePassword" :aria-pressed="showPassword">
                  <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                </button>
              </div>
            </div>

            <transition name="fade">
              <p v-if="error" class="form-error">{{ error }}</p>
            </transition>

            <button type="submit" class="submit-btn" :disabled="!canSubmit">
              <span class="shine"></span>
              <span v-if="loading"><i class="bi bi-arrow-repeat spin"></i> Processing...</span>
              <span v-else><i class="bi bi-box-arrow-in-right"></i> Sign In</span>
            </button>
          </form>

          <div class="form-note">
            <i class="bi bi-shield-lock"></i>
            <span>Data is protected with enterprise-grade security.</span>
          </div>
        </section>
      </div>

      <footer class="card-footer">
        (c) {{ new Date().getFullYear() }} {{ configStore.websiteTitle() }}
      </footer>
    </div>
  </div>
</template>

<style scoped>
.login-screen {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(0.75rem, 2vw, 1.5rem);
  background: radial-gradient(circle at top left, rgba(3, 25, 59, 0.9), rgba(6, 12, 25, 0.95)),
    linear-gradient(135deg, #050b17, #0f182e);
  position: relative;
  overflow: hidden;
  color: #e6edf7;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.35;
}

.orb-1 {
  width: 200px;
  height: 200px;
  top: -70px;
  right: -60px;
  background: rgba(217, 131, 36, 0.4);
}

.orb-2 {
  width: 160px;
  height: 160px;
  bottom: -60px;
  left: -50px;
  background: rgba(64, 92, 255, 0.28);
}

.orb-3 {
  width: 150px;
  height: 150px;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(217, 131, 36, 0.2);
}

.card-shell {
  width: min(360px, 100%);
  padding: clamp(1.15rem, 2vw, 1.5rem);
  border-radius: 16px;
  background: rgba(8, 14, 28, 0.94);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 18px 40px rgba(5, 11, 23, 0.45);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.form-panel {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.form-panel h2 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.form-panel > p {
  margin: 0;
  color: rgba(226, 232, 240, 0.68);
  font-size: 0.78rem;
}

.form-shell {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.form-field label {
  display: block;
  margin-bottom: 0.3rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: rgba(226, 232, 240, 0.85);
  font-size: 0.8rem;
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(13, 22, 43, 0.72);
  transition: border 0.2s ease, box-shadow 0.2s ease;
}

.input-wrapper:focus-within {
  border-color: rgba(217, 131, 36, 0.75);
  box-shadow: 0 8px 22px rgba(217, 131, 36, 0.18);
}

.input-wrapper input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  font-size: 0.85rem;
}

.input-wrapper input::placeholder {
  color: rgba(148, 163, 184, 0.58);
}

.toggle-password {
  border: none;
  background: none;
  color: rgba(148, 163, 184, 0.72);
  cursor: pointer;
  padding: 0.1rem 0.3rem;
  border-radius: 6px;
  transition: background 0.2s ease, color 0.2s ease;
}

.toggle-password:hover {
  background: rgba(148, 163, 184, 0.16);
  color: #fff;
}

.form-error {
  margin: 0;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  background: rgba(220, 38, 38, 0.12);
  color: #fca5a5;
  font-size: 0.78rem;
}

.submit-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.03em;
  border: none;
  color: #fff;
  background: linear-gradient(135deg, rgba(217, 131, 36, 0.92), rgba(217, 131, 36, 0.78));
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 32px rgba(217, 131, 36, 0.28);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.shine {
  position: absolute;
  inset: -40%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.28), transparent 70%);
  opacity: 0;
  transition: opacity 0.25s ease;
}

.submit-btn:hover .shine {
  opacity: 1;
}

.spin {
  animation: spin 1s linear infinite;
}

.form-note {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  background: rgba(15, 24, 48, 0.6);
  border-radius: 10px;
  border: 1px dashed rgba(148, 163, 184, 0.24);
  padding: 0.55rem 0.65rem;
  font-size: 0.75rem;
  color: rgba(226, 232, 240, 0.74);
}

.form-note i {
  color: var(--accent-color);
}

.card-footer {
  text-align: center;
  color: rgba(226, 232, 240, 0.65);
  font-size: 0.72rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 480px) {
  .login-screen {
    padding: 1.25rem 1rem;
  }

  .card-shell {
    padding: 1.2rem;
    border-radius: 14px;
  }
}
</style>
